import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Header, MobileNav } from '../../components/Header';
import { ProviderCard } from '../../components/ProviderCard';
import { ProvidersMap } from '../../components/ProvidersMap';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { providersAPI, matchingAPI } from '../../lib/api';
import { SERVICE_TYPES, getCityCoords, getCityName } from '../../lib/constants';
import { MagnifyingGlass, Dog, House, MapPin, Heart, ArrowRight, MapTrifold, List, Lightning, Star, CheckCircle } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const serviceIcons = {
  passeio_diario: Dog,
  hospedagem: House,
  visita_domicilio: MapPin,
  dog_sitting: Heart,
};

export default function ClientHome() {
  const { user } = useAuth();
  const [providers, setProviders] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [searchQuery, setSearchQuery] = useState('');

  const cityCoords = getCityCoords(user?.city);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const data = await providersAPI.getProviders({ city: user?.city });
        setProviders(data);
      } catch (error) {
        toast.error('Erro ao carregar prestadores');
      } finally {
        setLoading(false);
      }
    };

    fetchProviders();
  }, [user?.city]);

  useEffect(() => {
    if (!user?.city) return;
    const now = new Date();
    const day = (now.getDay() + 6) % 7;
    const time = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    matchingAPI.getMatches(user.city, null, day, time)
      .then(data => setMatches(data.matches || []))
      .catch(() => {});
  }, [user?.city]);

  const filteredProviders = providers.filter(p => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      p.user?.name?.toLowerCase().includes(query) ||
      p.bio?.toLowerCase().includes(query) ||
      p.services?.some(s => s.type?.toLowerCase().includes(query))
    );
  });

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Header />
      
      <main className="container mx-auto px-4 py-6">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-2xl sm:text-3xl font-bold font-outfit">
            Olá, {user?.name?.split(' ')[0]}! 
          </h1>
          <p className="text-muted-foreground mt-1">
            Encontre o melhor cuidado para o seu pet em {getCityName(user?.city)}
          </p>
        </motion.div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="relative">
            <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Pesquisar prestadores..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 rounded-full"
              data-testid="search-input"
            />
          </div>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="text-lg font-semibold mb-4">Serviços</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {SERVICE_TYPES.map((service) => {
              const Icon = serviceIcons[service.id] || Dog;
              return (
                <Link 
                  key={service.id} 
                  to={`/client/providers?service=${service.id}`}
                  data-testid={`service-${service.id}`}
                >
                  <Card className="service-card cursor-pointer h-full">
                    <CardContent className="p-4 text-center">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                        <Icon weight="duotone" className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="font-medium text-sm">{service.name}</h3>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </motion.div>

        {/* Matching Suggestions */}
        {matches.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <Lightning weight="fill" className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-semibold">Sugestões para Si</h2>
              <Badge variant="secondary" className="text-xs">Matching</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="matching-suggestions">
              {matches.map(({ provider: p, match_score }) => (
                <Link key={p.provider_id} to={`/client/provider/${p.provider_id}`}>
                  <Card className="provider-card cursor-pointer hover:border-amber-400 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0">
                          <span className="font-bold text-amber-600 text-sm">{match_score}%</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{p.user?.name || 'Prestador'}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-0.5">
                              <Star weight="fill" className="w-3 h-3 text-amber-500" />
                              {(p.rating || 0).toFixed(1)}
                            </span>
                            {p.identity_verified && (
                              <span className="flex items-center gap-0.5 text-green-600">
                                <CheckCircle weight="fill" className="w-3 h-3" /> Verificado
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {p.bio && <p className="text-xs text-muted-foreground line-clamp-2">{p.bio}</p>}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        {/* Providers Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Prestadores Perto de Si</h2>
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-full"
                data-testid="view-list"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('map')}
                className="rounded-full"
                data-testid="view-map"
              >
                <MapTrifold className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {(() => {
            if (loading) {
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <Card key={i} className="h-64 animate-pulse bg-muted" />
                  ))}
                </div>
              );
            }
            if (viewMode === 'map') {
              return (
                <ProvidersMap
                  providers={filteredProviders}
                  center={[cityCoords.lat, cityCoords.lng]}
                  zoom={12}
                  height="500px"
                />
              );
            }
            if (filteredProviders.length === 0) {
              return (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Dog weight="duotone" className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Nenhum prestador encontrado</h3>
                    <p className="text-muted-foreground text-sm">
                      Ainda não há prestadores na sua zona. Sejá o primeiro!
                    </p>
                  </CardContent>
                </Card>
              );
            }
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProviders.map((provider) => (
                  <ProviderCard key={provider.provider_id} provider={provider} />
                ))}
              </div>
            );
          })()}

          {filteredProviders.length > 0 && (
            <div className="text-center mt-6">
              <Link to="/client/providers">
                <Button variant="outline" className="rounded-full" data-testid="see-all-providers">
                  Ver Todos os Prestadores
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          )}
        </motion.div>
      </main>

      <MobileNav />
    </div>
  );
}
